import React, { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { formatDistance } from 'date-fns'
import { enUS, he, id } from 'date-fns/locale'

import { GetImageMetdataLink } from '@makyoapp/makyo-sdk'
import { FileCategory, IFilePresenter, ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import * as Mime from '@makyoapp/makyo-icon/mimetype'
import { Checkbox, Loading, Table, Td, Th, Tr, TrNotFound } from '@makyoapp/makyo-ui'

import { useStorageContext } from '@/context/StorageContext'
import useAuth from '@/hooks/useAuth'
import { getFileSize } from '@/shared/utils'
import { getMimeIcon } from '@/components/FileManager/MyFilesPanel'

import { StorageMoreDropdown } from './StorageMoreDropdown'
import { StorageTypeIcon } from './StorageTypeIcon'

const tableColumnfields: Array<keyof IFilePresenter> = ['_Name', '_Category', '_Size', '_CreatedAt']

/**
 * Storage File List
 */
export function StorageFileList() {
  const { t } = useTranslation()

  // Access Storage Context
  const {
    storage,
    checkedFiles,
    setCheckedFiles,
    activeCategory,
    setActiveCategory,
    isGridView,
    setPreview,
    isLoadingFiles,
  } = useStorageContext()

  // Derived
  const fileItems = storage.items
  const isEmpty = fileItems.length === 0
  const isAllSelected = checkedFiles.length === storage.items.length

  // Handle selecting individual file
  const handleSelectFile = (file: IFilePresenter) => {
    const isSelected = !!checkedFiles.find((c) => c._id === file._id)

    // Also update the checks state to keep both in sync
    if (isSelected) {
      setCheckedFiles(checkedFiles.filter((c) => c._id !== file._id))
    } else {
      setCheckedFiles([...checkedFiles, file])
    }
  }

  // Handle select all files
  const handleSelectAllFiles = () => {
    setCheckedFiles(isAllSelected ? [] : storage.items)
  }

  const handleRowDragStart = (e: React.DragEvent<HTMLTableRowElement>, file: IFilePresenter) => {
    // If the file being dragged is part of the selected files, drag all selected files
    // Otherwise, just drag the individual file
    const isPartOfSelection = checkedFiles.some((f) => f._id === file._id)
    const filesToDrag = isPartOfSelection && checkedFiles.length > 0 ? checkedFiles.map((f) => f._id) : [file._id]

    e.dataTransfer.setData('text/plain', JSON.stringify(filesToDrag))
    e.dataTransfer.setData('text/data-type', 'files')
    e.dataTransfer.effectAllowed = 'move'
  }

  // Render storage files as Table
  const RenderTable = () => {
    return (
      <div id='storage-table' className='rounded-sm overflow-hidden border bdr bg-white dark:bg-slate-800'>
        <Table className='bdr storage-table'>
          <thead>
            <Tr>
              <th className='chk' id='storage-check-all'>
                <Checkbox
                  className='grow-0'
                  labelClassName='[&>div]:size-4.5'
                  checked={isAllSelected}
                  onClick={handleSelectAllFiles}
                />
              </th>
              {tableColumnfields.map((f) => {
                return <StorageHead key={f} field={f} />
              })}
              <th className='act' />
            </Tr>
          </thead>

          <tbody>
            {fileItems.length > 0 ? (
              fileItems.map((it, index) => {
                const checked = !!checkedFiles.find((c) => c._id === it._id)
                const isLastRow = index === fileItems.length - 1
                return (
                  <Tr
                    key={it._id}
                    className={isLastRow ? 'border-b-0' : 'border-b border-slate-700'}
                    style={isLastRow ? { borderBottom: 'none !important' } : {}}
                    draggable
                    onDragStart={(e) => handleRowDragStart(e, it)}
                  >
                    <td className='chk'>
                      <Checkbox
                        labelClassName='[&>div]:size-4.5'
                        checked={checked}
                        onClick={() => handleSelectFile(it)}
                      />
                    </td>
                    {tableColumnfields.map((f) => {
                      return <StorageCell key={`${it._id}-${f}`} field={f} item={it} />
                    })}
                    <td className='act'>
                      <StorageMoreDropdown item={it} />
                    </td>
                  </Tr>
                )
              })
            ) : (
              <TrNotFound colSpan={tableColumnfields.length + 2} />
            )}
          </tbody>
        </Table>
      </div>
    )
  }

  // Render storage files as Grid
  const RenderGrid = () => {
    return (
      <div id='storage-grid' className='grid grid-cols-4 gap-4'>
        {fileItems.map((it) => {
          const checked = !!checkedFiles.find((c) => c._id === it._id)
          return (
            <div
              role='button'
              key={it._id}
              className='group relative rounded-lg border bdr bg-white dark:bg-slate-800 hover:bg-zinc-50 dark:hover:bg-slate-700 p-4 flex flex-col'
              onClick={(e) => {
                if (
                  [FileCategory.Image, FileCategory.Audio, FileCategory.Video, FileCategory.Document].includes(
                    it._Category
                  ) ||
                  it._Link.toLowerCase().endsWith('pdf')
                ) {
                  setPreview(it)
                }
              }}
            >
              <div
                className='flex items-center justify-between absolute top-0 left-0 right-0 px-2 pt-2'
                onClick={(e) => e.stopPropagation()}
              >
                <div>
                  <Checkbox
                    labelClassName='[&>div]:size-4.5'
                    checked={checked}
                    className='z-10'
                    onClick={() => handleSelectFile(it)}
                  />
                </div>
                <div>
                  <StorageMoreDropdown item={it} />
                </div>
              </div>

              <div className='pt-6'>
                <StorageGridPreview it={it} />
              </div>

              <div className='flex w-full mt-4'>
                <div className='w-full txt'>
                  <a
                    target='_blank'
                    rel='noreferrer noopener'
                    className='w-full mb-2 txt font-semibold max-w-[80%] text-left'
                    href={it._Link}
                    onClick={(e) => {
                      if (
                        [FileCategory.Image, FileCategory.Audio, FileCategory.Video, FileCategory.Document].includes(
                          it._Category
                        ) ||
                        it._Link.toLowerCase().endsWith('pdf')
                      ) {
                        e.preventDefault()
                        setPreview(it)
                      }
                    }}
                  >
                    {it._Name}
                  </a>
                  <div className='text-light'>
                    {t('Size: {{size}}', {
                      size: getFileSize(it._Size),
                    })}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <section id='storage-table-section-list'>
      <div id='storage-table-header' className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-4'>
          <h2 className='text-m-l font-semibold'>{t('Files')}</h2>
          {!isEmpty && (
            <div className='flex items-center gap-2'>
              <Checkbox
                labelClassName='[&>div]:size-4'
                label={`${checkedFiles.length > 0 ? `Selected ${checkedFiles.length} file(s)` : 'Select all files'}`}
                checked={isAllSelected}
                onClick={handleSelectAllFiles}
              />
            </div>
          )}
        </div>

        <div className='flex items-center gap-2 px-4 py-2 font-medium'>
          {activeCategory && (
            <>
              <span className=''>Showing {activeCategory}</span>
              <button className='ml-2 text-primary-600' onClick={() => setActiveCategory(undefined)}>
                Clear filter
              </button>
            </>
          )}
        </div>
      </div>

      {isLoadingFiles ? (
        <div className='flex justify-center items-center h-full min-h-[200px]'>
          <Loading fullHeight={false} />
        </div>
      ) : storage.items.length > 0 ? (
        <>{isGridView ? <RenderGrid /> : <RenderTable />}</>
      ) : (
        <div className='text-center p-10 mk-bg-50 rounded-lg border bdr border-dashed text-light'>
          {t('No files found')}
        </div>
      )}
    </section>
  )
}

function StorageHead({ field }: { field: keyof IFilePresenter }) {
  switch (field) {
    case '_Name':
      return (
        //? Th can only receive textTransform none in style
        <Th className='name-long normal-case' style={{ textTransform: 'none' }}>
          <Trans>Name</Trans>
        </Th>
      )
    case '_CreatedAt':
      return (
        <Th className='date' style={{ textTransform: 'none' }}>
          <Trans>Added On</Trans>
        </Th>
      )
    case '_Size':
      return (
        <Th style={{ textTransform: 'none' }}>
          <Trans>Size</Trans>
        </Th>
      )
    case '_Category':
      return (
        <Th className='w-[20%]' style={{ textTransform: 'none' }}>
          <Trans>Type</Trans>
        </Th>
      )
    default:
      return <Th>{field}</Th>
  }
}

function StorageCell({ field, item }: { field: keyof IFilePresenter; item: IFilePresenter }) {
  const { userData } = useAuth()
  const { setPreview } = useStorageContext()
  const [err, setErr] = useState('')
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (err && imgRef.current) {
      imgRef.current.src = 'https://via.placeholder.com/32'
      setTimeout(() => {
        if (imgRef.current) {
          imgRef.current.src = err
          setErr('')
        }
      }, 8000)
    }
  }, [field, err, imgRef])

  switch (field) {
    case '_Name': {
      const mime = getMimeIcon(item._Link)
      const Icon = Mime[mime]
      const metadataLink = GetImageMetdataLink(item._Link, ImageMetadata.XS)
      return (
        <Td className='name-long'>
          <div className='flex-c txt'>
            <a
              href={item._Link}
              target='_blank'
              rel='noopener noreferrer'
              className='flex-c txt'
              onClick={(e) => {
                if (
                  [FileCategory.Image, FileCategory.Audio, FileCategory.Video, FileCategory.Document].includes(
                    item._Category
                  ) ||
                  item._Link.toLowerCase().endsWith('pdf')
                ) {
                  e.preventDefault()
                  setPreview(item)
                }
              }}
            >
              {item._Category === FileCategory.Image ? (
                <>
                  <div className='aspect-square h-6 flex items-center'>
                    <img
                      alt=''
                      ref={imgRef}
                      loading='lazy'
                      src={metadataLink}
                      className={clsx('w-full h-auto object-contain', err && 'mk-img-err')}
                      onError={() => {
                        setErr(metadataLink)
                      }}
                    />
                  </div>

                  {/* {err && (
                    <Mime.Image
                      className='shrink-0'
                      width={24}
                      height={24}
                    />
                  )} */}
                </>
              ) : (
                Icon && <Icon className='shrink-0' width={24} height={24} />
              )}
              <div className='txt'>{item._Name}</div>
            </a>
          </div>
        </Td>
      )
    }
    case '_Category':
      return (
        <Td className='sto-cat'>
          <div className='flex-c'>
            <StorageTypeIcon ty={item._Category} size='small' svgProps={{ width: 16, height: 16 }} />
            {`${item._Category[0].toUpperCase()}${item._Category.substring(1)}`}
          </div>
        </Td>
      )
    case '_Size':
      return <Td className='sto-size'>{getFileSize(item._Size)}</Td>
    case '_CreatedAt': {
      const lang = userData?.lang
      let locale = undefined
      switch (lang) {
        case 'en':
          locale = enUS
          break
        case 'he':
          locale = he
          break
        case 'id':
          locale = id
          break
      }
      const created = new Date(item._CreatedAt)
      const now = new Date()
      return (
        <Td className='sto-date'>
          {formatDistance(created, now, {
            addSuffix: true,
            locale,
          })}
        </Td>
      )
    }
    default:
      return <Td>{item[field]?.toString()}</Td>
  }
}

function StorageGridPreview({ it }: { it: IFilePresenter }) {
  let el

  switch (it._Category) {
    case FileCategory.Image: {
      const link = GetImageMetdataLink(it._Link, ImageMetadata.XS)
      el = <img alt='' src={link} loading='lazy' className='w-full h-full object-cover' />
      break
    }
    case FileCategory.Video: {
      el = <video src={it._Link} autoPlay={false} />
      break
    }
    default: {
      const mime = getMimeIcon(it._Link)
      const Icon = Mime[mime]
      el = <Icon width={72} height={72} />
    }
  }

  return <div className='preview aspect-square w-full flex-cc overflow-hidden rounded-sm'>{el}</div>
}
